import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Card, Col, Row, Tooltip } from 'antd';
import { useTranslate, TFnType } from '@tolgee/react';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  numDisplay,
  RMBSymbol,
  currencyDisplay,
  getAmountByCurrency,
  currencyDisplayWithUnit,
} from 'views/Dashboard/ImplementData/utils';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { RegionFixedExchangeRateValue } from 'services/config/region';
import Empty from 'components/Empty';
import { useSize } from 'ahooks';
import { useDashboardPlatformTips } from '../Filter/useFilter';

const Container = styled(Card)`
  margin-top: 16px;
  border-radius: 4px;
  min-height: 420px;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  .ant-card-head {
    border-bottom-color: var(--fett-border-default);
  }
  .ant-card-head-title {
    color: var(--fett-text-primary);
  }
  width: 100%;
  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3370ff;
        display: inline-block;
        margin-right: 4px;
      }
      .anticon {
        margin-left: 4px;
      }
    }
  }
  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 12px 24px;
    .ant-row {
      height: 50px;
      margin-bottom: 12px;
      background-color: var(--fett-bg-subtle);
      border-radius: 4px;
      align-items: center;
      padding: 0 16px;
      .title {
        color: var(--fett-text-tertiary);
      }
      .num {
        color: var(--fett-text-secondary);
        font-size: 16px;
      }
    }
  }
`;
const Left = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  .pie {
    width: 100%;
    height: 210px;
  }
`;
const Relative = styled.div`
  position: relative;
  min-width: 0;
  flex: 1 1 320px;
  > .bar {
    min-height: 320px;
  }
  width: 100%;
  .ant-empty {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .bar {
    width: 100%;
    height: 100%;
  }
`;
interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const barOption = (
  value: Dashboard.OrderOverviewInner,
  t: TFnType,
  tips: ReturnType<typeof useDashboardPlatformTips>,
  currency?: RegionFixedExchangeRateValue,
): EChartsOption => {
  const data = value['COD/非COD每日下单'] || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  return {
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [
        {
          name: t('ViewDashboard.COD下单量'),
          itemStyle: {
            color: '#5B8FF9',
          },
          lineStyle: {
            color: '#5b8ff9',
          },
        },
        {
          name: t('ViewDashboard.非COD下单量'),
          itemStyle: {
            color: '#5AD8A6',
          },
          lineStyle: {
            color: '#5b8ff9',
          },
          tooltip: {
            show: Boolean(tips.非COD下单量tips),
            formatter: tips.非COD下单量tips,
          },
        },
        {
          name: t('ViewDashboard.COD下单总金额'),
          itemStyle: {
            color: '#f6903d',
          },
          lineStyle: {
            color: '#f6903d',
          },
        },
        {
          name: t('ViewDashboard.非COD下单总金额'),
          itemStyle: {
            color: '#7262fd',
          },
          lineStyle: {
            color: '#7262fd',
          },
          tooltip: {
            show: Boolean(tips.非COD下单总金额tips),
            formatter: tips.非COD下单总金额tips,
          },
        },
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: `(${currency?.symbol ?? RMBSymbol})`,
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.COD下单量),
        type: 'bar',
        name: t('ViewDashboard.COD下单量'),
        itemStyle: {
          color: '#5B8FF9',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => x.非COD下单量),
        type: 'bar',
        name: t('ViewDashboard.非COD下单量'),
        itemStyle: {
          color: '#5AD8A6',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.COD下单总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) =>
            `${currency?.symbol ?? RMBSymbol}${currencyDisplay(Number(value))}`,
        },
        name: t('ViewDashboard.COD下单总金额'),
        lineStyle: {
          color: '#f6903d',
          opacity: 0.85,
        },
        itemStyle: {
          color: '#f6903d',
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.非COD下单总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value) =>
            `${currency?.symbol ?? RMBSymbol}${currencyDisplay(Number(value))}`,
        },
        name: t('ViewDashboard.非COD下单总金额'),
        lineStyle: {
          color: '#7262fd',
          opacity: 0.85,
        },
        itemStyle: {
          color: '#7262fd',
        },
      },
    ],
  };
};
const pieOption = (v: Dashboard.OrderOverviewInner, t: TFnType): EChartsOption => {
  return {
    backgroundColor: '#f5f9fe',

    series: [
      {
        type: 'pie',
        radius: '50%',
        label: {
          formatter: '{b}\n{c} ({d}%)',
        },
        data: [
          {
            value: v.COD下单量,
            name: t('ViewDashboard.COD下单量'),
            label: {
              overflow: 'break',
            },
            itemStyle: {
              color: '#5B8FF9',
              opacity: 0.85,
            },
          },
          {
            value: v.非COD下单量,
            name: t('ViewDashboard.非COD下单量'),
            label: {
              overflow: 'break',
            },
            itemStyle: {
              color: '#5AD8A6',
              opacity: 0.85,
            },
          },
        ],
      },
    ],
  };
};

interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const PlaceOrder = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { currency } = useDashboardCurrency();
  const chart1DomRef = useRef<HTMLDivElement>(null);
  const chart2DomRef = useRef<HTMLDivElement>(null);
  const chart1Ref = useRef<echarts.ECharts>();
  const chart2Ref = useRef<echarts.ECharts>();

  const { width: width1 } = useSize(chart1DomRef) || {};
  const { width: width2 } = useSize(chart2DomRef) || {};

  useEffect(() => {
    chart1Ref.current?.resize();
  }, [width1]);
  useEffect(() => {
    chart2Ref.current?.resize();
  }, [width2]);
  useEffect(() => {
    if (!value || !chart1DomRef.current) {
      return;
    }
    if (!chart1Ref.current) {
      chart1Ref.current = initEcharts(chart1DomRef.current);
    }
    const options = pieOption(value, t);
    chart1Ref.current.setOption(applyChartTheme(options));
  }, [value, t, currency?.symbol]);

  const tips = useDashboardPlatformTips();

  useEffect(() => {
    if (!value || !chart2DomRef.current) {
      return;
    }
    if (!chart2Ref.current) {
      chart2Ref.current = initEcharts(chart2DomRef.current);
    }
    const options = barOption(value, t, tips, currency);
    chart2Ref.current.setOption(applyChartTheme(options));
  }, [value, t, tips, currency]);

  return (
    <Container
      title={
        <div>
          <span className="before" />

          {t('ViewDashboard.COD非COD下单情况')}
          <Tooltip title={t('ViewDashboard.COD非COD下单tooltip')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      }
    >
      <Left>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.下单总量')}
            {tips.下单总量tips && (
              <Tooltip title={tips.下单总量tips}>
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            )}
          </Col>
          <Col span={14} className="num">
            {numDisplay(value?.COD非COD下单总量)}
          </Col>
        </Row>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.下单总金额')}
            {tips.下单总金额tips && (
              <Tooltip title={tips.下单总金额tips}>
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            )}
          </Col>
          <Col span={14} className="num">
            {currencyDisplayWithUnit(currency?.shorthand, value?.COD非COD下单总金额)}
          </Col>
        </Row>

        <Relative className="pie">
          <div
            className="pie"
            ref={chart1DomRef}
            style={{ display: !value?.非COD下单量 && !value?.COD下单量 ? 'none' : 'block' }}
          />
          {!value?.非COD下单量 && !value?.COD下单量 ? <Empty /> : null}
        </Relative>
      </Left>
      <Relative>
        <div className="bar" ref={chart2DomRef} />
        {!value?.['COD/非COD每日下单']?.length ? <Empty /> : null}
      </Relative>
    </Container>
  );
};

export default PlaceOrder;
